import {useEffect, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {authDestination} from './authContinuation';
import {fetchWithAuth} from '../../utils/fetchWithAuth';

export default function PluginAuthorizationPage() {
  const {search} = useLocation();
  const request = new URLSearchParams(search).get('request');
  const valid = /^[A-Za-z0-9_-]{43}$/.test(request || '');
  const returnTo = `/client/plugin-connect?request=${request}`;
  const signedIn = !!localStorage.getItem('token');
  const [details, setDetails] = useState(null);
  const [updates, setUpdates] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!valid || !signedIn) return;
    let cancelled = false;
    fetchWithAuth(`/api/plugins/authorization/${request}`).then(async response => {
      const value = await response.json();
      if (!response.ok) throw Error(value.error || 'Could not load this connection request.');
      if (!cancelled) { setDetails(value); setUpdates(value.requested_scopes.includes('pabs:write')); }
    }).catch(problem => { if (!cancelled) setError(problem.message); });
    return () => { cancelled = true; };
  }, [request, signedIn, valid]);
  async function decide(allow) {
    setBusy(true); setError('');
    try {
      const response = await fetchWithAuth(`/api/plugins/authorization/${request}`, { method: 'POST',
        headers: {'Content-Type': 'application/json'}, body: JSON.stringify({allow, scope: updates ? 'pabs:read pabs:write' : 'pabs:read'}) });
      const value = await response.json();
      if (!response.ok) throw Error(value.error || 'Could not connect. Please try again.');
      window.location.assign(value.redirect_url);
    } catch (problem) { setError(problem.message); setBusy(false); }
  }
  return <main className="min-h-screen bg-brand-50 px-5 py-12 text-brand-900 sm:py-20">
    <div className="mx-auto max-w-xl">
      <Link to="/" aria-label="Pabs home"><img src="/images/pablo_logo1.0.svg" alt="Pabs" className="mb-10 h-10 w-10"/></Link>
      <h1 className="text-3xl font-semibold leading-tight">Connect your AI tool to Pabs</h1>
      {!valid ? <p role="alert" className="mt-5">This connection request is invalid. Start Connect again in your AI tool.</p> : <>
        {error && <p role="alert" className="mt-5 text-red-800">{error}</p>}
        {!signedIn ? <>
          <p className="mt-5 leading-relaxed">Sign in or create an account to let your AI tool work with your Pabs projects. You’ll choose which product to work on in each conversation.</p>
          <div className="mt-6 flex flex-wrap items-center gap-5">
            <Link to={authDestination('/register', returnTo)} className="inline-flex rounded-full bg-brand-900 px-5 py-3 font-semibold text-white">Create a Pabs account</Link>
            <Link to={authDestination('/login', returnTo)} className="font-semibold underline underline-offset-4">Sign in to Pabs</Link>
          </div>
        </> : !details && !error ? <p role="status" className="mt-5">Loading the connection request…</p> : details ? <>
          <p className="mt-5 leading-relaxed"><strong>{details.client_name}</strong> is asking to connect to your Pabs account. It will be able to list your projects and read the strategy, decisions and design guidance for the product you choose in a conversation.</p>
          {details.requested_scopes.includes('pabs:write') && <label className="mt-6 flex items-start gap-3 leading-relaxed">
            <input type="checkbox" checked={updates} disabled={busy} onChange={event => setUpdates(event.target.checked)} className="mt-1 h-5 w-5 shrink-0 accent-brand-900"/>
            <span>Also let it set up new products, return learnings and perform product work when I ask. Actions that require my decision still need it.</span>
          </label>}
          <p className="mt-5 leading-relaxed">Your AI tool may send Pabs a summary of the product and relevant evidence. Files and other services remain subject to the access you give that tool.</p>
          <p className="mt-4 break-words text-sm leading-relaxed text-brand-700">You’ll return to {details.callback_origin} after connecting. You can disconnect this tool in Pabs under Connections → AI tools.</p>
          <div className="mt-8 flex flex-wrap gap-4">
            <button type="button" onClick={() => decide(true)} disabled={busy} className="rounded-full bg-brand-900 px-5 py-3 font-semibold text-white disabled:opacity-60">{busy ? 'Returning to your AI tool…' : 'Connect Pabs'}</button>
            <button type="button" onClick={() => decide(false)} disabled={busy} className="px-3 py-3 font-semibold underline underline-offset-4 disabled:opacity-60">Cancel</button>
          </div>
        </> : null}
      </>}
    </div>
  </main>;
}
